const PUBLIC_PRICING_TIERS = [
  {
    // Light tier - new entry-level product. The public offer is always discounted,
    // so /pricing shows the same $49 -> $24 framing users see at checkout.
    id: 'light',
    type: 'subscription',
    name: 'Light',
    price: 24,
    originalPrice: 49,
    productSlug: 'light-plan',
    tagline: 'Build, backtest, and run one live trading bot.',
    capacity: { liveBots: 1, backtestHours: 33, revisions: 15, support: 'Community' },
    bestFor: '',
    features: [
      { label: '50 AI messages per month', detail: 'Chat with the AI to design, iterate, and refine your strategy.' },
      { label: '15 AI strategy revisions per month', detail: 'Iterate prompts up to 15 times monthly to improve performance.' },
      { label: '33 backtesting hours per month', detail: 'About 2,000 minutes of historical sims each billing cycle to validate ideas before going live.' },
      { label: 'Run up to 1 live bot at a time', detail: 'Your account can have one running live bot at a time.' },
      { label: 'Broker and market data access', detail: 'Connect supported brokers and market-data sources. Some premium data sources may require their own data subscription.' },
      { label: 'SEC filings and public market research data', detail: 'Ask the AI Agent for company filings, SEC fundamentals, insider transactions, 13F holdings, macro indicators, Treasury data, labor data, GDP, industry and regional economic data, funding data, and futures positioning.' },
      { label: 'Strategy marketplace access', detail: 'Browse and use marketplace strategies.' },
      { label: 'Community support', detail: 'Get ideas, examples, and product updates from the BotSpot community.' },
    ],
    cta: 'Start with Light',
    useCartCheckout: false,
    ctaLink: '/checkout/light-plan',
    featured: false,
    theme: 'purple',
    discountPercent: 50,
  },
  {
    id: 'starter',
    type: 'subscription',
    name: 'Starter',
    price: 49,
    originalPrice: 99,
    productSlug: 'starter-plan',
    tagline: 'More backtesting and room to run two live bots.',
    capacity: { liveBots: 2, backtestHours: 200, revisions: 100, support: 'Community' },
    bestFor: '',
    features: [
      { label: 'Everything in Light', detail: 'Includes broker/data access, marketplace access, and community support.' },
      { label: 'Unlimited AI chat messages', detail: 'Chat messages are currently uncapped for paid plans. Strategy revisions still have monthly limits.' },
      { label: '100 AI strategy revisions per month', detail: 'Iterate prompts up to 100 times monthly to improve performance.' },
      { label: '200 backtesting hours per month', detail: 'Use 200 hours of historical sims each month to validate ideas.' },
      { label: 'Run up to 2 live bots at a time', detail: 'Your account can have two running live bots at the same time.' },
    ],
    cta: 'Get Started',
    // Starter uses StarterCheckoutPage for the Light-vs-Starter checkout experiment.
    // This centralizes all starter-paywall purchases through the same bucketing surface.
    useCartCheckout: false,
    ctaLink: '/checkout/starter-plan?product=starter-plan',
    featured: true,
    theme: 'purple',
    discountPercent: 50,
  },
  {
    id: 'pro',
    type: 'subscription',
    name: 'Pro',
    price: 199,
    productSlug: 'pro-plan',
    tagline: 'Live Algo Lab, support calls, and more running bots.',
    capacity: { liveBots: 5, backtestHours: 500, revisions: 300, support: '2 calls + live lab' },
    bestFor: '',
    features: [
      { label: 'Everything in Starter', detail: 'Includes Starter limits plus the Pro benefits below.' },
      { label: 'Unlimited AI chat messages', detail: 'Chat messages are currently uncapped for paid plans. Strategy revisions still have monthly limits.' },
      { label: '300 AI strategy revisions per month', detail: 'Triple the Starter revision room to refine bots faster.' },
      { label: '500 backtesting hours per month', detail: 'More compute to stress-test and harden strategies.' },
      { label: 'Run up to 5 live bots at a time', detail: 'Your account can have five running live bots at the same time.' },
      { label: '2 support calls per month', detail: 'Two one-on-one expert sessions for guidance and troubleshooting.' },
      {
        label: 'Live Algo Lab every Tuesday',
        detail: 'Join Rob live, influence what gets built, and get help when you are stuck.',
        link: { to: '/live-algo-lab', text: 'View Live Algo Lab' },
      },
      {
        label: 'Live Algo Lab replay archive',
        detail: 'Catch up on past builds and revisit the bots, prompts, and decisions from each session.',
        link: { to: '/live-algo-lab', text: 'View Live Algo Lab' },
      },
    ],
    cta: 'Unlock Pro',
    useCartCheckout: true,
    featured: false,
    theme: 'purple',
  },
  {
    id: 'all-access',
    type: 'subscription',
    name: 'All Access',
    price: 399,
    productSlug: 'all-access-plan',
    tagline: 'Highest limits, faster help, and deeper reviews.',
    capacity: { liveBots: 10, backtestHours: 2000, revisions: 500, support: '4 calls + priority' },
    bestFor: '',
    features: [
      { label: 'Everything in Pro', detail: 'Includes Live Algo Lab, support calls, marketplace access, broker/data access, and Pro benefits.' },
      { label: 'Unlimited AI chat messages', detail: 'Chat messages are currently uncapped for paid plans. Strategy revisions still have monthly limits.' },
      { label: '500 AI strategy revisions per month', detail: 'High-volume iteration headroom for teams and power users.' },
      { label: '2000 backtesting hours per month', detail: 'Enterprise-level backtesting capacity for heavier testing.' },
      { label: 'Run up to 10 live bots at a time', detail: 'Your account can have ten running live bots at the same time.' },
      { label: '4 support calls per month', detail: 'Four dedicated expert sessions for planning and optimization.' },
      {
        label: 'Live Algo Lab plus priority help',
        detail: 'Join the Tuesday lab and get faster attention for strategy questions during live sessions and follow-up.',
        link: { to: '/live-algo-lab', text: 'View Live Algo Lab' },
      },
      { label: 'Priority setup help', detail: 'Faster help getting accounts, bots, and workflows set up.' },
      { label: 'Strategy review sessions', detail: 'Expert review sessions to refine high-usage strategies.' },
    ],
    cta: 'Go All Access',
    useCartCheckout: true,
    featured: false,
    theme: 'purple',
  },
  {
    id: 'business',
    type: 'enterprise',
    name: 'Business',
    priceLabel: 'Custom',
    tagline: 'Custom pricing, SLAs, and a dedicated account manager',
    bestFor: 'Teams needing custom capacity, SLAs, and connectors',
    features: [
      { label: 'Bots & backtesting', detail: 'Custom capacity sized to your team and workloads.' },
      { label: 'Account manager', detail: 'Dedicated owner for onboarding, rollouts, and escalations.' },
      { label: 'Support & SLAs', detail: 'Custom response times and channels to fit your ops needs.' },
      { label: 'Security & compliance', detail: 'Custom reviews, DPAs, and audit support to match governance.' },
      { label: 'Broker/venue expansion', detail: 'Custom connectors or venues added on request.' },
    ],
    cta: 'Talk to Our Team',
    ctaLink: '/enterprise',
    isExternal: false,
    theme: 'purple',
  },
  {
    id: 'bootcamp-live',
    type: 'course',
    name: 'Live AI Bootcamp',
    price: 2999,
    tagline: '5-week live cohort with recordings, projects, and Q&A.',
    bestFor: 'Builders who want live help and accountability',
    features: [
      { label: 'Starts August 12, 2026', detail: 'The next live cohort is planned for Wednesday evenings at 7 PM Eastern.' },
      { label: 'From 5 payments of $700', detail: 'Installment plans are available on the Bootcamp page. One-time payment saves money.' },
      { label: 'Live sessions and Q&A', detail: 'Join the class live, ask questions, and get help while you build.' },
      { label: 'Recordings and materials included', detail: 'Live buyers get the recordings, resources, and course portal access.' },
      { label: 'Self-paced purchase credits toward live', detail: 'If someone starts self-paced first, what they paid can be applied toward a live seat later.' },
    ],
    cta: 'Compare Bootcamp options',
    ctaLink: '/ai-bot-builder-bootcamp',
    featured: false,
    theme: 'purple',
  },
  {
    id: 'bootcamp-self-paced',
    type: 'course',
    name: 'Self-Paced AI Bootcamp',
    price: 499,
    originalPrice: 997,
    productSlug: 'ai-bootcamp-self-paced',
    planKey: 'self-paced-launch',
    tagline: 'Start today with the Bootcamp recordings and materials.',
    bestFor: 'Builders who want the course without live sessions',
    features: [
      { label: 'Launch price $499', detail: 'Normal price is $997, so the current launch savings is $498.' },
      { label: 'Bootcamp recording library', detail: 'Watch the course recordings on your own schedule.' },
      { label: 'Course resources and materials', detail: 'Use available recordings, summaries, and course resources as they are published.' },
      { label: '30 days of Pro access', detail: 'Use BotSpot Pro while you work through the recordings.' },
      { label: 'Upgrade credit toward live', detail: 'Apply the self-paced purchase amount toward a future live Bootcamp seat.' },
    ],
    cta: 'Start self-paced',
    useCartCheckout: true,
    featured: false,
    theme: 'purple',
    discountPercent: 50,
  },
];

function formatPublicPrice(price) {
  if (typeof price === 'number') {
    return `$${price.toLocaleString('en-US')}`;
  }
  return price ? `$${price}` : '';
}

function pricePeriodForTier(tier) {
  if (tier.priceLabel) return '';
  if (tier.type === 'subscription') return 'per month';
  if (tier.type === 'course') return 'one-time';
  return '';
}

function publicPricingSummary(tier) {
  const period = pricePeriodForTier(tier);
  const currentPrice = tier.priceLabel || formatPublicPrice(tier.price);
  const priceText = period ? `${currentPrice} ${period}` : currentPrice;
  return {
    id: tier.id,
    name: tier.name,
    type: tier.type,
    price: priceText,
    originalPrice: tier.originalPrice ? formatPublicPrice(tier.originalPrice) : undefined,
    discountPercent: tier.discountPercent,
    tagline: tier.tagline,
    bestFor: tier.bestFor || undefined,
    capacity: tier.capacity,
    features: tier.features.map((feature) => feature.label),
    featureDetails: tier.features.map((feature) => ({
      label: feature.label,
      detail: feature.detail,
    })),
    cta: tier.cta,
    ctaLink: tier.ctaLink,
    productSlug: tier.productSlug,
    planKey: tier.planKey,
  };
}

const CORE_SUBSCRIPTION_PLANS = PUBLIC_PRICING_TIERS.filter((tier) =>
  ['light', 'starter', 'pro', 'all-access'].includes(tier.id)
);
const BUSINESS_PRICING_PLAN = PUBLIC_PRICING_TIERS.find((tier) => tier.id === 'business');
const EDUCATION_PRICING_PLANS = PUBLIC_PRICING_TIERS.filter((tier) =>
  ['bootcamp-self-paced', 'bootcamp-live'].includes(tier.id)
);

module.exports = {
  BUSINESS_PRICING_PLAN,
  CORE_SUBSCRIPTION_PLANS,
  EDUCATION_PRICING_PLANS,
  PUBLIC_PRICING_TIERS,
  formatPublicPrice,
  publicPricingSummary,
};
